"use client";

import { createContext, useContext, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import type { ClientAnalyticsProvider } from "@/lib/analytics/types";
import { nullClientProvider } from "@/lib/analytics/providers/null";
import { envClient } from "@/lib/config/env.client";

const AnalyticsContext = createContext<ClientAnalyticsProvider>(nullClientProvider);

export function useAnalytics(): ClientAnalyticsProvider {
  return useContext(AnalyticsContext);
}

export function AnalyticsClientProvider({
  enabled,
  provider,
  children,
}: {
  enabled: boolean;
  provider?: string;
  children: React.ReactNode;
}) {
  const [clientProvider, setClientProvider] =
    useState<ClientAnalyticsProvider>(nullClientProvider);
  const pathname = usePathname();

  useEffect(() => {
    let active = true;
    if (!enabled) {
      setClientProvider(nullClientProvider);
      return;
    }

    const load = async () => {
      switch (provider) {
        case "posthog": {
          if (!envClient.NEXT_PUBLIC_POSTHOG_KEY) {
            setClientProvider(nullClientProvider);
            return;
          }
          const { PostHogClientProvider } = await import(
            "@/lib/analytics/providers/posthog.client"
          );
          if (active) {
            setClientProvider(
              new PostHogClientProvider(
                envClient.NEXT_PUBLIC_POSTHOG_KEY,
                envClient.NEXT_PUBLIC_POSTHOG_HOST
              )
            );
          }
          break;
        }
        case "umami": {
          if (!envClient.NEXT_PUBLIC_UMAMI_WEBSITE_ID) {
            setClientProvider(nullClientProvider);
            return;
          }
          const { UmamiClientProvider } = await import(
            "@/lib/analytics/providers/umami.client"
          );
          if (active) {
            setClientProvider(
              new UmamiClientProvider(
                envClient.NEXT_PUBLIC_UMAMI_WEBSITE_ID,
                envClient.NEXT_PUBLIC_UMAMI_HOST
              )
            );
          }
          break;
        }
        case "plausible": {
          if (!envClient.NEXT_PUBLIC_PLAUSIBLE_DOMAIN) {
            setClientProvider(nullClientProvider);
            return;
          }
          const { PlausibleClientProvider } = await import(
            "@/lib/analytics/providers/plausible.client"
          );
          if (active) {
            setClientProvider(
              new PlausibleClientProvider(
                envClient.NEXT_PUBLIC_PLAUSIBLE_DOMAIN,
                envClient.NEXT_PUBLIC_PLAUSIBLE_HOST
              )
            );
          }
          break;
        }
        default:
          setClientProvider(nullClientProvider);
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, [enabled, provider]);

  const lastPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname || lastPathnameRef.current === pathname) return;
    lastPathnameRef.current = pathname;
    clientProvider.page?.(pathname);
  }, [clientProvider, pathname]);

  return (
    <AnalyticsContext.Provider value={clientProvider}>{children}</AnalyticsContext.Provider>
  );
}
